<template>
  <div>
    <PageHeader
      eyebrow="Month-Wise Departures"
      title="Umrah Packages with Flights & Hotels"
      subtitle="Compare pricing by room type — Sharing, Quad, Triple, Double, or Couple — across our upcoming monthly departures. All packages include return flights and hotel stays in Makkah &amp; Madina."
    />

    <section class="container mx-auto px-4 py-16">
      <!-- Month Filter -->
      <div class="flex flex-wrap items-center justify-center gap-2 mb-12">
        <button
          @click="selectedMonth = 'All'"
          :class="[
            'rounded-full px-4 py-2 text-sm font-semibold transition',
            selectedMonth === 'All' ? 'bg-amber-500 text-white shadow-md shadow-amber-500/30' : 'bg-white border border-gray-200 text-gray-600 hover:border-amber-300 hover:text-amber-600'
          ]"
        >
          All Months
        </button>
        <button
          v-for="month in availableMonths"
          :key="month"
          @click="selectedMonth = month"
          :class="[
            'rounded-full px-4 py-2 text-sm font-semibold transition',
            selectedMonth === month ? 'bg-amber-500 text-white shadow-md shadow-amber-500/30' : 'bg-white border border-gray-200 text-gray-600 hover:border-amber-300 hover:text-amber-600'
          ]"
        >
          {{ month }}
        </button>
      </div>

      <!-- Room Type Legend -->
      <div class="mx-auto mb-10 max-w-3xl rounded-2xl border border-amber-100 bg-amber-50/60 px-6 py-4 text-sm text-gray-700">
        <span class="font-semibold text-gray-900">Room types explained:</span>
        Sharing (6–8 pilgrims per room), Quad (4 per room), Triple (3 per room), Double (2 per room), Couple (private double for a married couple).
      </div>

      <!-- Package Cards -->
      <div class="space-y-8">
        <PackagePricingCard
          v-for="pkg in filteredPackages"
          :key="pkg.title"
          v-bind="pkg"
          :whatsapp-number="whatsappNumber"
        />
      </div>

      <p v-if="filteredPackages.length === 0" class="text-center text-gray-500 py-12">
        No packages currently listed for this month — please check another month or contact us for custom dates.
      </p>
    </section>
  </div>
</template>

<script>
import PageHeader from '../components/PageHeader.vue'
import PackagePricingCard from '../components/PackagePricingCard.vue'
import { listItems } from '../services/dataStore'
import economyImage from '../assets/images/madina/madina-daytime-wide.jpg'
import executiveImage from '../assets/images/madina/madina-rooftop-sunset.webp'
import familyImage from '../assets/images/madina/madina-aerial-blue-night.jpg'
import luxuryImage from '../assets/images/madina/madina-sunset-panorama.jpg'
import ramadanImage from '../assets/images/madina/madina-dome-night-closeup.jpg'

const coupleImage = 'https://images.unsplash.com/photo-1590490360182-c33d57733427?w=800&q=80'
const extendedFamilyImage = 'https://images.unsplash.com/photo-1611048267451-e6ed903d4a38?w=800&q=80'
const friendsImage = 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=800&q=80'
const ladiesGroupImage = 'https://images.unsplash.com/photo-1542816417-0983c9c9ad53?w=800&q=80'
const vipImage = 'https://images.unsplash.com/photo-1580418827493-f2b22c0a76cb?w=800&q=80'

export default {
  name: 'PackagesPage',
  components: {
    PageHeader,
    PackagePricingCard
  },
  data() {
    return {
      whatsappNumber: '923185073318',
      selectedMonth: 'All',
      packages: [
        {
          title: 'Economy Umrah Package',
          category: 'Economy',
          image: economyImage,
          nights: '7 Nights / 8 Days',
          cities: '4 nights Makkah • 3 nights Madina',
          hotels: ['Makkah: Al Kiswah Towers', 'Madina: Dar Al Eiman Royal'],
          months: ['August 2026', 'September 2026', 'October 2026'],
          flightIncluded: true,
          pricing: [
            { type: 'Sharing (6-8 pax)', price: 125000 },
            { type: 'Quad', price: 145000 },
            { type: 'Triple', price: 165000 },
            { type: 'Double', price: 195000 },
            { type: 'Couple', price: 210000 }
          ]
        },
        {
          title: 'Executive Umrah Retreat',
          category: 'Premium',
          image: executiveImage,
          nights: '10 Nights / 11 Days',
          cities: '6 nights Makkah • 4 nights Madina',
          hotels: ['Makkah: Jabal Omar Hyatt Regency', 'Madina: The Oberoi'],
          months: ['September 2026', 'October 2026', 'November 2026'],
          flightIncluded: true,
          pricing: [
            { type: 'Sharing (6-8 pax)', price: 165000 },
            { type: 'Quad', price: 195000 },
            { type: 'Triple', price: 225000 },
            { type: 'Double', price: 265000 },
            { type: 'Couple', price: 295000 }
          ]
        },
        {
          title: 'Family Comfort Package',
          category: 'Value Plus',
          image: familyImage,
          nights: '12 Nights / 13 Days',
          cities: '7 nights Makkah • 5 nights Madina',
          hotels: ['Makkah: Hilton Convention', 'Madina: Pullman Zamzam'],
          months: ['October 2026', 'November 2026', 'December 2026'],
          flightIncluded: true,
          pricing: [
            { type: 'Sharing (6-8 pax)', price: 145000 },
            { type: 'Quad', price: 175000 },
            { type: 'Triple', price: 205000 },
            { type: 'Double', price: 245000 },
            { type: 'Couple', price: 265000 }
          ]
        },
        {
          title: 'Luxury Quad Stay',
          category: 'Signature',
          image: luxuryImage,
          nights: '7 Nights / 8 Days',
          cities: '5 nights Makkah • 2 nights Madina',
          hotels: ['Makkah: Swissôtel Al Maqam (Kaaba view)', 'Madina: Anwar Al Madinah Mövenpick'],
          months: ['November 2026', 'December 2026', 'January 2027'],
          flightIncluded: true,
          pricing: [
            { type: 'Sharing (6-8 pax)', price: 155000 },
            { type: 'Quad', price: 185000 },
            { type: 'Triple', price: 210000 },
            { type: 'Double', price: 245000 },
            { type: 'Couple', price: 270000 }
          ]
        },
        {
          title: 'Ramadan Special Umrah Package',
          category: 'Ramadan Special',
          image: ramadanImage,
          nights: '15 Nights / 16 Days',
          cities: '8 nights Makkah • 7 nights Madina',
          hotels: ['Makkah: Swissôtel Al Maqam (Kaaba view)', 'Madina: Anwar Al Madinah Mövenpick'],
          months: ['March 2027', 'April 2027'],
          flightIncluded: true,
          pricing: [
            { type: 'Sharing (6-8 pax)', price: 285000 },
            { type: 'Quad', price: 325000 },
            { type: 'Triple', price: 365000 },
            { type: 'Double', price: 425000 },
            { type: 'Couple', price: 465000 }
          ]
        },
        {
          title: "Couple's Private Umrah Package",
          category: 'Couples',
          image: coupleImage,
          nights: '7 Nights / 8 Days',
          cities: '4 nights Makkah • 3 nights Madina',
          hotels: ['Makkah: Swissôtel Al Maqam (Kaaba view)', 'Madina: The Oberoi', 'Private double room throughout, no sharing'],
          months: ['September 2026', 'October 2026', 'November 2026'],
          flightIncluded: true,
          pricing: [
            { type: 'Double (Private Room)', price: 220000 },
            { type: 'Couple (Kaaba View Upgrade)', price: 260000 }
          ]
        },
        {
          title: 'Extended Family Umrah Package',
          category: 'Families',
          image: extendedFamilyImage,
          nights: '14 Nights / 15 Days',
          cities: '8 nights Makkah • 6 nights Madina',
          hotels: ['Makkah: Jabal Omar Hyatt Regency (connecting rooms)', 'Madina: Pullman Zamzam Madina', 'Kids stay & eat free (under 6)'],
          months: ['November 2026', 'December 2026', 'January 2027'],
          flightIncluded: true,
          pricing: [
            { type: 'Sharing (6-8 pax)', price: 155000 },
            { type: 'Quad', price: 185000 },
            { type: 'Triple', price: 215000 },
            { type: 'Double', price: 255000 },
            { type: 'Family Suite (5-6 pax)', price: 235000 }
          ]
        },
        {
          title: 'Friends Group Umrah Package',
          category: 'Friends',
          image: friendsImage,
          nights: '9 Nights / 10 Days',
          cities: '5 nights Makkah • 4 nights Madina',
          hotels: ['Makkah: Hilton Convention Makkah', 'Madina: Elaf Taiba Hotel', 'Free group WhatsApp coordinator'],
          months: ['August 2026', 'September 2026', 'December 2026'],
          flightIncluded: true,
          pricing: [
            { type: 'Sharing (6-8 pax)', price: 135000 },
            { type: 'Quad', price: 160000 },
            { type: 'Triple', price: 180000 }
          ]
        },
        {
          title: 'Ladies Group Umrah Package',
          category: 'Ladies Group',
          image: ladiesGroupImage,
          nights: '10 Nights / 11 Days',
          cities: '6 nights Makkah • 4 nights Madina',
          hotels: ['Makkah: Swissôtel Al Maqam (Kaaba view)', 'Madina: Anwar Al Madinah Mövenpick', 'Dedicated female group coordinator & mahram-compliant arrangements'],
          months: ['October 2026', 'November 2026', 'March 2027'],
          flightIncluded: true,
          pricing: [
            { type: 'Sharing (6-8 pax)', price: 150000 },
            { type: 'Quad', price: 175000 },
            { type: 'Triple', price: 200000 },
            { type: 'Double', price: 235000 }
          ]
        },
        {
          title: 'Royal VIP Luxury Umrah Package',
          category: 'Ultra Luxury',
          image: vipImage,
          nights: '10 Nights / 11 Days',
          cities: '6 nights Makkah • 4 nights Madina',
          hotels: ['Makkah: Raffles Makkah Palace (Kaaba view suite)', 'Madina: The Oberoi', 'Private chauffeur & VIP fast-track Haram access'],
          months: ['December 2026', 'January 2027', 'February 2027'],
          flightIncluded: true,
          pricing: [
            { type: 'Double Suite', price: 450000 },
            { type: 'Couple (Royal Suite)', price: 520000 }
          ]
        }
      ]
    }
  },
  async mounted() {
    this.packages = await listItems('packages', this.packages)
  },
  computed: {
    availableMonths() {
      const months = new Set()
      this.packages.forEach(pkg => pkg.months.forEach(m => months.add(m)))
      return Array.from(months)
    },
    filteredPackages() {
      if (this.selectedMonth === 'All') {
        return this.packages
      }
      return this.packages.filter(pkg => pkg.months.includes(this.selectedMonth))
    }
  }
}
</script>
